// /c/join?code=…: what joining a class means — the consent text and its
// version, the policy in plain words — and "I agree and join", which sends
// the version the person read (RFC-043 M3, byj-1wv.4.1). The consent screen
// is JoinView, which the instructor's preview of a student's view draws too.
import { useEffect, useState, type FormEvent } from "react";
import { CardPage } from "../components/CardPage";
import { AssignmentCommands, JoinView, ShareCommand, useJoin } from "../components/JoinView";
import { navigate } from "../router";
import { t } from "../i18n";

export function JoinClass({ initialCode }: { initialCode: string }) {
  const [code, setCode] = useState(initialCode);
  const [entered, setEntered] = useState(initialCode);
  const [joined, setJoined] = useState<{ id: string; name: string } | null>(null);
  const { preview, error, stale, busy, look, agree: join } = useJoin();
  useEffect(() => {
    if (code) void look(code);
  }, [code, look]);
  const agree = async () => {
    const c = await join();
    if (c) setJoined(c);
  };

  // each step on the sign-in pages' card (components/CardPage, byj-1wv.51.7)
  if (joined) {
    return (
      <CardPage title={t("classes:joinedTitle", { name: joined.name })} back={{ to: `/c/${encodeURIComponent(joined.id)}`, label: t("classes:openClassPage") }}>
        <p className="flush">{t("classes:joinedLead")}</p>
        <AssignmentCommands code={code} assignments={preview?.assignments ?? []} />
        <ShareCommand code={code} />
        <p className="muted flush">{t("classes:joinedAfter")}</p>
      </CardPage>
    );
  }

  if (!code) {
    const submit = (e: FormEvent) => {
      e.preventDefault();
      const c = entered.trim();
      if (!c) return;
      navigate(`/c/join?code=${encodeURIComponent(c)}`, true);
      setCode(c);
    };
    return (
      <CardPage title={t("classes:joinClassTitle")}>
        <form className="stack" onSubmit={submit}>
          <label>
            {t("classes:joinCode")}
            <input
              className="code-input"
              value={entered}
              onChange={(e) => setEntered(e.target.value)}
              placeholder={t("classes:joinCodePlaceholder")}
              autoCapitalize="characters"
              autoComplete="off"
            />
          </label>
          <button className="primary" type="submit" disabled={!entered.trim()}>
            {t("classes:continueJoin")}
          </button>
        </form>
      </CardPage>
    );
  }

  // what joining means: the join view draws its own heading, the class's name
  return (
    <CardPage back={{ to: "/classes", label: t("classes:classesTitle") }}>
      {error && <p className="error">{error}</p>}
      {!preview ? !error && <p className="muted">{t("common:loading")}</p> : <JoinView preview={preview} stale={stale} busy={busy} onAgree={() => void agree()} />}
    </CardPage>
  );
}
